/**
 * Monthly Revenue Trend Chart (Highcharts)
 * Line/area chart showing revenue trend over time, stacked by master channel when "All" selected
 *
 * Labeling: Uses category x-axis with YYYY-MM -> "Jan 2026" formatting. No Date/Date.parse
 * so labels cannot shift due to timezone. Backend must only emit months in the selected period.
 */
import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getChannelColorVar } from '../../../utils/channelColorUtils.js';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Format "YYYY-MM" to "Jan 2026" using string parts only — no Date, no timezone. */
function formatMonthLabel(monthKey) {
  if (!monthKey || typeof monthKey !== 'string') return monthKey || '';
  const [y, m] = monthKey.split('-');
  const monthIndex = parseInt(m, 10) - 1;
  const name = (monthIndex >= 0 && monthIndex < 12) ? MONTH_NAMES[monthIndex] : m;
  return `${name} ${y || ''}`.trim();
}

export default function MonthlyRevenueTrendChart({ data = [], masterChannels = [], selectedChannel = null }) {
  const options = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        title: { text: 'Monthly Revenue Trend' },
        subtitle: { text: 'No data available for this period/channel' },
        series: [],
      };
    }

    const categories = data.map(d => formatMonthLabel(d.month));

    const series = selectedChannel
      ? [{
          name: masterChannels.find(mc => mc.code === selectedChannel)?.name || selectedChannel,
          data: data.map((d, i) => [i, d[selectedChannel] || 0]),
          color: getChannelColorVar(selectedChannel),
        }]
      : masterChannels.map(mc => ({
          name: mc.name,
          data: data.map((d, i) => [i, d[mc.code] || 0]),
          color: getChannelColorVar(mc.code || mc.master_channel_key),
        }));

    return {
      chart: {
        type: 'area',
        height: 350,
      },
      title: {
        text: 'Monthly Revenue Trend',
        style: { fontSize: '16px', fontWeight: '600' },
      },
      xAxis: {
        type: 'category',
        categories,
        title: { text: 'Month' },
      },
      yAxis: {
        title: { text: 'Revenue ($)' },
        labels: {
          formatter: function() {
            return '$' + (this.value / 1000).toFixed(0) + 'k';
          },
        },
      },
      tooltip: {
        shared: true,
        backgroundColor: '#ffffff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        style: { color: '#111827' },
        formatter: function() {
          const idx = (this.points && this.points[0]) ? this.points[0].x : (this.point && this.point.x);
          const label = (idx != null && categories[idx] !== undefined) ? categories[idx] : '';
          let tooltip = '<b>' + label + '</b><br/>';
          this.points.forEach(point => {
            tooltip += '<span style="color:' + point.color + '">●</span> ' + point.series.name + ': <b>$' +
              Highcharts.numberFormat(point.y, 0) + '</b><br/>';
          });
          return tooltip;
        },
      },
      plotOptions: {
        area: {
          stacking: selectedChannel ? null : 'normal', // Stack only when showing all channels
          lineColor: '#666666',
          lineWidth: 1,
          marker: {
            lineWidth: 1,
            lineColor: '#666666',
          },
          dataLabels: {
            enabled: true,
            formatter: function() {
              // Only show labels for values > $1000 to avoid clutter
              if (this.y > 1000) {
                return '$' + (this.y / 1000).toFixed(1) + 'k';
              }
              return '';
            },
            style: {
              fontSize: '11px',
              fontWeight: '500',
              textOutline: '1px contrast',
            },
          },
        },
      },
      series,
      credits: { enabled: false },
    };
  }, [data, masterChannels, selectedChannel]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}
